/**
 * CRM Fields Card
 *
 * Compact card showing CRM fields cache status with refresh button.
 * Used in RoutingTab and DeliveryRulesTab for easy field refresh.
 *
 * User scenarios:
 * 1. First time: No fields cached → "Click Refresh to load" → user clicks Refresh
 * 2. Fields loaded: Shows count and status → fields appear in QueryBuilder
 * 3. CRM schema changed: User clicks Refresh → fields updated
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Database, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCrmFields, useRefreshCrmFields } from '@/features/routing/hooks';
import { formatDurationCoarse } from '@/lib/format-duration';
import type {
  CrmEntityType,
  CrmFieldsAllResponse,
  CrmFieldsEntityResponse,
} from '@/lib/api/types';

interface CrmFieldsCardProps {
  /** Entity type to filter fields (optional - shows all if not set) */
  entityType?: CrmEntityType;
  /** Compact mode - less padding */
  compact?: boolean;
}

export function CrmFieldsCard({ entityType, compact }: CrmFieldsCardProps) {
  const { t } = useTranslation('settings');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get CRM fields to show status
  const { data: crmFieldsResponse, isLoading: crmLoading } = useCrmFields({
    entityType,
    enabled: true,
  });

  // Refresh mutation
  const refreshMutation = useRefreshCrmFields();

  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    try {
      // Refresh all entity types
      const entityTypes: CrmEntityType[] = ['deal', 'lead', 'contact', 'company'];
      for (const type of entityTypes) {
        await refreshMutation.mutateAsync(type);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  // Parse response to check status. Without an entityType prop the API returns
  // CrmFieldsAllResponse — a map keyed by entity type, not a single-entity
  // object. Reading only the single-entity shape made this card claim
  // "Not cached" forever on the project Delivery tab even with a warm cache,
  // and Refresh looked like a no-op (it updated the cache, then the map came
  // back and was misread as empty again).
  const responseData = crmFieldsResponse?.data;

  const isSingleEntity =
    !!responseData && 'fields' in responseData && 'entityType' in responseData;

  let fieldCount = 0;
  let cacheStatus: string | undefined;
  let cacheAgeMinutes: number | undefined;

  if (isSingleEntity) {
    const single = responseData as CrmFieldsEntityResponse;
    fieldCount = Array.isArray(single.fields) ? single.fields.length : 0;
    cacheStatus = single.cacheStatus;
    cacheAgeMinutes = single.cacheAgeMinutes;
  } else if (responseData) {
    const entries = Object.values(responseData as CrmFieldsAllResponse).filter(
      (entry) => entry && Array.isArray(entry.fields)
    );
    fieldCount = entries.reduce((sum, entry) => sum + entry.fields.length, 0);
    if (entries.length > 0) {
      cacheStatus = 'cached';
      const oldestUpdate = entries
        .map((entry) => (entry.updatedAt ? Date.parse(entry.updatedAt) : NaN))
        .filter((ts) => Number.isFinite(ts))
        .reduce((min, ts) => Math.min(min, ts), Infinity);
      if (Number.isFinite(oldestUpdate)) {
        cacheAgeMinutes = Math.max(0, Math.floor((Date.now() - oldestUpdate) / 60000));
      }
    }
  }

  // Format cache age
  const formatCacheAge = (minutes?: number) =>
    minutes ? formatDurationCoarse(minutes * 60) : null;

  const cacheAge = formatCacheAge(cacheAgeMinutes);
  const hasCachedFields = fieldCount > 0 || cacheStatus === 'cached';
  // Cache is stale if older than 24 hours (1440 minutes)
  const isStale = cacheAgeMinutes !== undefined && cacheAgeMinutes > 1440;

  return (
    <Card className={compact ? 'p-3' : undefined}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Database className="h-5 w-5 text-primary shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-fg">
                {t('routing.crmFieldsTitle', 'CRM Fields')}
              </h3>
              {/* Status badge */}
              {!crmLoading &&
                (hasCachedFields ? (
                  isStale ? (
                    <Badge variant="warning" className="text-xs">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {t('routing.crmFieldsStale', 'Stale')}
                    </Badge>
                  ) : (
                    <Badge variant="success" className="text-xs">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {fieldCount > 0
                        ? t('routing.crmFieldsCount', '{{count}} fields', {
                            count: fieldCount,
                          })
                        : t('routing.crmFieldsCachedShort', 'Cached')}
                    </Badge>
                  )
                ) : (
                  <Badge variant="warning" className="text-xs">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {t('routing.crmFieldsNotCached', 'Not cached')}
                  </Badge>
                ))}
              {/* Cache age */}
              {cacheAge && (
                <span className="text-xs text-fg-muted flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {t('routing.crmFieldsAgo', '{{time}} ago', { time: cacheAge })}
                </span>
              )}
            </div>
            <p className="text-sm text-fg-muted mt-0.5">
              {crmLoading
                ? t('routing.crmFieldsLoading', 'Loading CRM fields...')
                : hasCachedFields
                  ? isStale
                    ? t(
                        'routing.crmFieldsStaleDescription',
                        'Cache is over 24 hours old. Refresh to get latest fields.'
                      )
                    : t(
                        'routing.crmFieldsDescription',
                        'Fields available for rule conditions'
                      )
                  : t('routing.crmFieldsEmpty', 'Click Refresh to load fields from CRM')}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size={compact ? 'sm' : 'md'}
          onClick={handleRefreshAll}
          disabled={isRefreshing || crmLoading}
          className="gap-2 shrink-0"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing
            ? t('routing.crmFieldsRefreshing', 'Refreshing...')
            : t('routing.crmFieldsRefresh', 'Refresh')}
        </Button>
      </div>
    </Card>
  );
}
